import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, isValid, parse } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Select } from "components/ui/Select/Select";
import {
  isInfluenzaOverdue,
  isPneumococcalOverdue,
} from "features/cdm/utils/vaccineOverdue";
import { useLatestCdmVaccineDoses } from "features/immunizations/hooks/useLatestCdmVaccineDoses";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import {
  COUNT_OPTIONS,
  COVID_VACCINE_ELSEWHERE_PROMPT,
  VACCINE_OPTIONS,
  YES_NO_PATIENT_DECLINED_OPTIONS,
} from "../../../../../constants/formOptions";
import style from "./PlanAndOutcomeForm.module.css";

const VACCINE_DISABLED_VALUES = ["NO", "DECLINED_BY_PATIENT"];
const VACCINE_DATE_REQUIRED_VALUES = ["YES", "GIVEN_ELSEWHERE"];

const ddmmyyyyToIso = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const parsed = parse(ddmmyyyy, "dd-MM-yyyy", new Date());
  return isValid(parsed) ? format(parsed, "yyyy-MM-dd") : "";
};

const isoToDdmmyyyy = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  const parsed = parse(yyyymmdd, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "dd-MM-yyyy") : "";
};

const formSchema = () =>
  z
    .object({
      edAttendancesLast6Months: z
        .string()
        .min(1, "ED attendances last 6 months is required"),
      unscheduledAdmissionsLast6Months: z
        .string()
        .min(1, "Unscheduled admissions last 6 months is required"),
      influenzaVaccine: z.string().min(1, "Influenza vaccine is required"),
      influenzaVaccineDate: z.string(),
      pneumococcalVaccine: z
        .string()
        .min(1, "Pneumococcal vaccine is required"),
      pneumococcalVaccineDate: z.string(),
      covidVaccine: z.string().min(1, "COVID vaccine is required"),
      covidVaccineStatusUpToDate: z
        .string()
        .min(1, "COVID vaccine status up to date is required"),
    })
    .superRefine((values, context) => {
      if (
        VACCINE_DATE_REQUIRED_VALUES.includes(values.influenzaVaccine) &&
        !values.influenzaVaccineDate
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Influenza vaccine date is required",
          path: ["influenzaVaccineDate"],
        });
      }

      if (
        VACCINE_DATE_REQUIRED_VALUES.includes(values.pneumococcalVaccine) &&
        !values.pneumococcalVaccineDate
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Pneumococcal vaccine date is required",
          path: ["pneumococcalVaccineDate"],
        });
      }
    });

export const PlanAndOutcomeForm = ({
  onBack,
  onContinue,
  data,
  isReview,
  isLaoding,
}) => {
  const form = useForm({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      influenzaVaccine: data?.influenzaVaccine || "",
      influenzaVaccineDate: data?.influenzaVaccineDate || "",
      pneumococcalVaccine: data?.pneumococcalVaccine || "",
      pneumococcalVaccineDate: data?.pneumococcalVaccineDate || "",
      covidVaccine: data?.covidVaccine || "",
      covidVaccineStatusUpToDate: data?.covidVaccineStatusUpToDate || "",
      edAttendancesLast6Months: data?.edAttendancesLast6Months || "",
      unscheduledAdmissionsLast6Months:
        data?.unscheduledAdmissionsLast6Months || "",
    },
  });

  const influenzaVaccineValue = form.watch("influenzaVaccine");
  const pneumococcalVaccineValue = form.watch("pneumococcalVaccine");
  const covidVaccineValue = form.watch("covidVaccine");
  const isInfluenzaDateDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(influenzaVaccineValue);
  const isPneumococcalDateDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(pneumococcalVaccineValue);
  const isCovidStatusDisabled =
    isReview || VACCINE_DISABLED_VALUES.includes(covidVaccineValue);

  // Overdue prompts (Ref 31/33) — only while capturing, not on a past review.
  const patientId = useCurrentPatientId();
  const { data: latestVaccineDoses } = useLatestCdmVaccineDoses(
    isReview ? null : patientId
  );
  const showInfluenzaOverdue =
    !isReview && isInfluenzaOverdue(latestVaccineDoses?.influenzaLastDoseDate);
  const showPneumococcalOverdue =
    !isReview &&
    isPneumococcalOverdue(latestVaccineDoses?.pneumococcalLastDoseDate);

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PlanAndOutcomeForm"))}
      isReview={isReview}
      isLastStep={true}
      isLoading={isLaoding}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("PlanAndOutcomeForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Vaccinations</h3>
              <div
                className={style.inputGroupGrid2Columns}
                style={{
                  marginTop: "16px",
                }}
              >
                <FormField name="influenzaVaccine">
                  <label className={style.addReferralFormLabel}>
                    Influenza Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={form.watch("influenzaVaccine")}
                    onChange={(value) => {
                      form.setValue("influenzaVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("influenzaVaccineDate", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                  {showInfluenzaOverdue && (
                    <p className={style.vaccineOverduePrompt}>
                      Influenza vaccine overdue — no dose recorded this flu
                      season.
                    </p>
                  )}
                </FormField>

                <FormField name="influenzaVaccineDate">
                  <label className={style.addReferralFormLabel}>
                    Influenza Vaccine Date
                  </label>
                  <DatePickerField
                    value={ddmmyyyyToIso(form.watch("influenzaVaccineDate"))}
                    onChange={(isoDate) =>
                      form.setValue(
                        "influenzaVaccineDate",
                        isoToDdmmyyyy(isoDate),
                        { shouldValidate: true }
                      )
                    }
                    disabled={isInfluenzaDateDisabled}
                    maxDate={format(new Date(), "yyyy-MM-dd")}
                  />
                </FormField>
                <FormField name="pneumococcalVaccine">
                  <label className={style.addReferralFormLabel}>
                    Pneumococcal Polysaccharide Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={form.watch("pneumococcalVaccine")}
                    onChange={(value) => {
                      form.setValue("pneumococcalVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("pneumococcalVaccineDate", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                  {showPneumococcalOverdue && (
                    <p className={style.vaccineOverduePrompt}>
                      Pneumococcal vaccine overdue — no dose recorded in the
                      last 5 years.
                    </p>
                  )}
                </FormField>
                <FormField name="pneumococcalVaccineDate">
                  <label className={style.addReferralFormLabel}>
                    Pneumococcal Polysaccharide Vaccine Date
                  </label>
                  <DatePickerField
                    value={ddmmyyyyToIso(form.watch("pneumococcalVaccineDate"))}
                    onChange={(isoDate) =>
                      form.setValue(
                        "pneumococcalVaccineDate",
                        isoToDdmmyyyy(isoDate),
                        { shouldValidate: true }
                      )
                    }
                    disabled={isPneumococcalDateDisabled}
                    maxDate={format(new Date(), "yyyy-MM-dd")}
                  />
                </FormField>
                <FormField name="covidVaccine">
                  <label className={style.addReferralFormLabel}>
                    COVID Vaccine
                  </label>
                  <Select
                    options={VACCINE_OPTIONS}
                    placeholder="Select"
                    value={covidVaccineValue}
                    onChange={(value) => {
                      form.setValue("covidVaccine", value, {
                        shouldValidate: true,
                      });
                      if (VACCINE_DISABLED_VALUES.includes(value)) {
                        form.setValue("covidVaccineStatusUpToDate", "NO", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    disabled={isReview}
                    required
                  />
                  {/* GP prompt (client feedback): the patient's Covid vaccinations
                      may have been given elsewhere since the last recorded date, so
                      prompt the GP to confirm rather than assume the file is complete. */}
                  <p className={style.gpPrompt}>
                    {COVID_VACCINE_ELSEWHERE_PROMPT}
                  </p>
                </FormField>
                <FormField name="covidVaccineStatusUpToDate">
                  <label className={style.addReferralFormLabel}>
                    COVID Vaccine Status Up To Date
                  </label>
                  <Select
                    options={YES_NO_PATIENT_DECLINED_OPTIONS}
                    placeholder="Select"
                    value={form.watch("covidVaccineStatusUpToDate")}
                    onChange={(value) =>
                      form.setValue("covidVaccineStatusUpToDate", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isCovidStatusDisabled}
                    required
                  />
                </FormField>
              </div>
            </div>
            <div
              className={style.itemWrapper}
              style={{
                borderBottom: "none",
                marginBottom: "8px",
                paddingBottom: "0px",
              }}
            >
              <h3>Emergency Admissions</h3>
              <div
                className={style.inputGroupGrid2Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="edAttendancesLast6Months">
                  <label className={style.addReferralFormLabel}>
                    ED Attendances Last 6 Months
                  </label>
                  <Select
                    options={COUNT_OPTIONS}
                    placeholder="Select"
                    value={form.watch("edAttendancesLast6Months")}
                    onChange={(value) =>
                      form.setValue("edAttendancesLast6Months", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                </FormField>

                <FormField name="unscheduledAdmissionsLast6Months">
                  <label className={style.addReferralFormLabel}>
                    Unscheduled Admissions Last 6 Months
                  </label>
                  <Select
                    options={COUNT_OPTIONS}
                    placeholder="Select"
                    value={form.watch("unscheduledAdmissionsLast6Months")}
                    onChange={(value) =>
                      form.setValue("unscheduledAdmissionsLast6Months", value, {
                        shouldValidate: true,
                      })
                    }
                    disabled={isReview}
                    required
                  />
                </FormField>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </ItemFormWrapper>
  );
};
